import {
  Drawer,
  DrawerBody,
  DrawerCloseButton,
  DrawerContent,
  DrawerFooter,
  DrawerOverlay,
  DrawerProps,
  FormControl,
  FormLabel,
  Stack,
} from '@chakra-ui/react';
import { usePostSubAccountMutation } from 'api';
import { AccountResponse } from 'api/bindings/AccountResponse';
import { PostSubAccountRequest } from 'api/bindings/PostSubAccountRequest';
import { AppInput, FormButtonGroup } from 'features';
import { useCallback, useMemo } from 'react';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { toast } from 'toast';

export interface CreateSubAccountDrawerProps
  extends Omit<DrawerProps, 'onSubmit' | 'children'> {
  account: AccountResponse;
}

interface Form extends PostSubAccountRequest {}

export const CreateSubAccountDrawer = (props: CreateSubAccountDrawerProps) => {
  const { account, ...rest } = props;

  // Fetches
  const [postSubAccount] = usePostSubAccountMutation();

  // State
  const form = useForm<Form>({
    mode: 'all',
    defaultValues: {
      accountId: account.id,
    },
  });
  const { watch, reset, handleSubmit, control } = form;
  const [subAccountCode, subAccountName] = watch(['code', 'name']);
  const fullSubAccountCode = useMemo(
    () => `${account.code}.${subAccountCode}`,
    [account.code, subAccountCode]
  );
  const fullSubAccountName = useMemo(
    () => `${fullSubAccountCode} ${subAccountName}`,
    [fullSubAccountCode, subAccountName]
  );

  const onSubmit = useCallback(
    async (form: Form) => {
      try {
        const result = await postSubAccount(form);
        if ('error' in result) {
          toast({
            title: `Failed to post account ${fullSubAccountName}`,
            description: (result.error as any)?.data?.message,
            status: 'error',
          });
          return;
        }
        toast({
          title: `Account ${fullSubAccountName} posted successfully!`,
          status: 'success',
        });
        reset();
        rest.onClose();
      } catch {}
    },
    [fullSubAccountName, postSubAccount, reset, rest]
  );

  // Return
  return (
    <FormProvider {...form}>
      <form onSubmit={handleSubmit(onSubmit)}>
        <Drawer {...rest}>
          <DrawerOverlay />
          <DrawerCloseButton />
          <DrawerContent>
            <DrawerBody>
              <Stack spacing={4}>
                <FormControl>
                  <FormLabel htmlFor="code">Code:</FormLabel>
                  <Controller
                    control={control}
                    name="code"
                    rules={{
                      required: {
                        value: true,
                        message: `Sub-Account code is required.`,
                      },
                      min: {
                        value: 0,
                        message: `Sub-Account code must be zero or higher.`,
                      },
                      pattern: {
                        value: /^\d+$/,
                        message: 'Sub-Account code must be numeral.',
                      },
                    }}
                    render={(f) => (
                      <AppInput type="number" id="code" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="name">Name:</FormLabel>
                  <Controller
                    control={control}
                    name="name"
                    rules={{
                      required: {
                        value: true,
                        message: `Sub-Account name is required.`,
                      },
                      minLength: {
                        value: 3,
                        message: `Sub-Account name must be at least 3 characters.`,
                      },
                      maxLength: {
                        value: 256,
                        message: `Sub-Account name must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => <AppInput id="name" {...(f.field as any)} />}
                  />
                </FormControl>
                <FormControl>
                  <FormLabel htmlFor="description">Description:</FormLabel>
                  <Controller
                    control={control}
                    name="description"
                    rules={{
                      required: {
                        value: true,
                        message: 'Sub-Account description is required.',
                      },
                      maxLength: {
                        value: 256,
                        message: `Description must be no more than 256 characters.`,
                      },
                    }}
                    render={(f) => (
                      <AppInput id="description" {...(f.field as any)} />
                    )}
                  />
                </FormControl>
              </Stack>
            </DrawerBody>
            <DrawerFooter>
              <FormButtonGroup
                onConfirm={handleSubmit(onSubmit)}
                onCancel={rest.onClose}
              />
            </DrawerFooter>
          </DrawerContent>
        </Drawer>
      </form>
    </FormProvider>
  );
};
